import React from 'react';
import { Platform } from 'react-native';

import Container from '@src/components/Container';
import NewEntryFab from '@src/components/NewEntryFab';
import PostCard from '@src/components/PostCard';
import RefreshControl from '@src/components/RefreshControl';
import VisibleStatusBar from '@src/components/VisibleStatusBar';
import { idKeyExtractor } from '@src/configuration/constants';
import useAnimatedHeader from '@src/hooks/useAnimatedHeader';
import useAuthenticationTokens from '@src/hooks/useAuthenticationTokens';
import useViewableItems from '@src/hooks/useViewableItems';
import { Post } from '@src/types';

import Heading from './Heading';
import { List, ListFooter } from './styled';
import { Props } from './types';
import useUserPosts from './useUserPosts';

const isWeb = Platform.OS === 'web';

const Screen: React.FC<Props> = ({ route }) => {
  const {
    tokens: { userID },
  } = useAuthenticationTokens();
  const id = React.useMemo(() => {
    return route.params?.id || userID || '';
  }, [route.params, userID]);
  const timestamp = React.useMemo(() => Date.now(), []);
  const { data, handleEndReached, handleRefresh, refreshing } = useUserPosts(
    id,
    timestamp
  );
  const {
    handleViewableItemsChanged,
    currentVisibleIndex,
    viewabilityConfig,
  } = useViewableItems();
  const handleScroll = useAnimatedHeader();

  const handleRemovePost = React.useCallback(async () => {
    try {
      await handleRefresh();
    } catch (error) {
      console.log('[Error:profileScreen]', error);
    }
  }, [handleRefresh]);

  return (
    <>
      <VisibleStatusBar />
      <Container>
        <List
          onScroll={handleScroll}
          ListHeaderComponent={<Heading id={id} />}
          refreshControl={
            <RefreshControl refreshing={refreshing} onRefresh={handleRefresh} />
          }
          onViewableItemsChanged={handleViewableItemsChanged}
          viewabilityConfig={viewabilityConfig}
          onEndReached={handleEndReached}
          ListFooterComponent={<ListFooter />}
          renderItem={({ item, index }: { item: Post; index: number }) => (
            <PostCard
              play={index === currentVisibleIndex}
              data={item}
              onRemove={handleRemovePost}
            />
          )}
          keyExtractor={idKeyExtractor}
          data={data}
        />
        {!isWeb && <NewEntryFab />}
      </Container>
    </>
  );
};

Screen.displayName = 'Profile';

export default Screen;
